import type { Player } from './player';
import { unlockAudio } from './audio';

export interface Mouse {
  x: number;
  y: number;
  down: boolean;
}

export const isTouchDevice = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
// Auto-scale to fit any screen — reference design at 1280×720, never upscale
export const MOBILE_ZOOM = Math.min(1.0, window.innerWidth / 1280, window.innerHeight / 720);

export const keys = {
  brake: false,
  boost: false,
  touchSpecial: -1 as number,
  joyX: 0 as number,
  joyY: 0 as number,
  // P2
  up: false, down: false, left: false, right: false, fire: false,
};

export const coop = {
  enabled: localStorage.getItem('ns_coop_pixi') === '1',
};

export interface GameState {
  W: number;
  H: number;
  state: 'title' | 'playing' | 'paused' | 'gameover' | 'upgrades' | 'settings' | 'achievements' | 'tutorial' | 'victory';
  player: Player | null;
  mouse: Mouse;
  mouseHistory: Array<{ x: number; y: number }>;
  frameCount: number;
  endless: boolean;
  daily: boolean;
}

export const G: GameState = {
  W: window.innerWidth / MOBILE_ZOOM,
  H: window.innerHeight / MOBILE_ZOOM,
  state: 'playing',
  player: null,
  mouse: { x: window.innerWidth / MOBILE_ZOOM / 2, y: window.innerHeight / MOBILE_ZOOM / 2, down: false },
  mouseHistory: [],
  frameCount: 0,
  endless: localStorage.getItem('ns_endless') === '1',
  daily: false,
};

// Layout-aware key labels — KeyboardEvent.code is layout-agnostic (positional), but the
// user-facing label "Q" is wrong for AZERTY (where the QWERTY-Q position has "A" printed on the cap).
// At boot we read navigator.keyboard.getLayoutMap() and cache the printed-glyph for each code.
let keyLayoutMap: Map<string, string> | null = null;
export function initKeyLayout(): void {
  // @ts-ignore — Keyboard API is experimental in older browsers
  const kb = (navigator as any).keyboard;
  if (kb && typeof kb.getLayoutMap === 'function') {
    kb.getLayoutMap().then((m: Map<string, string>) => { keyLayoutMap = m; }).catch(() => {});
  }
}
export function keyLabel(code: string): string {
  const m = keyLayoutMap?.get(code);
  if (m) return m.toUpperCase();
  // Fallback: strip standard prefixes ("Key", "Digit", "Numpad", "Arrow")
  return code.replace(/^(Key|Digit|Numpad|Arrow)/, '').toUpperCase();
}

export function bindInput(): void {
  initKeyLayout();
  window.addEventListener('pointermove', (e) => {
    G.mouse.x = e.clientX / MOBILE_ZOOM;
    G.mouse.y = e.clientY / MOBILE_ZOOM;
  });
  window.addEventListener('pointerdown', () => { G.mouse.down = true; unlockAudio(); });
  window.addEventListener('pointerup', () => { G.mouse.down = false; });
  window.addEventListener('resize', () => {
    G.W = window.innerWidth / MOBILE_ZOOM;
    G.H = window.innerHeight / MOBILE_ZOOM;
  });
  window.addEventListener('keydown', (e) => {
    // P1 — main droite, loin des touches WASD P2
    if (e.code === 'ShiftRight') { keys.brake = true; e.preventDefault(); }
    if (e.code === 'ControlRight') keys.boost = true;
    // P2 — WASD (main gauche) + G (tir), séparé de la souris P1 (main droite)
    if (e.code === 'KeyW') { keys.up = true; e.preventDefault(); }
    if (e.code === 'KeyS') { keys.down = true; e.preventDefault(); }
    if (e.code === 'KeyA') { keys.left = true; e.preventDefault(); }
    if (e.code === 'KeyD') { keys.right = true; e.preventDefault(); }
    if (e.code === 'KeyG') keys.fire = true;
  });
  window.addEventListener('keyup', (e) => {
    if (e.code === 'ShiftRight') keys.brake = false;
    if (e.code === 'ControlRight') keys.boost = false;
    if (e.code === 'KeyW') keys.up = false;
    if (e.code === 'KeyS') keys.down = false;
    if (e.code === 'KeyA') keys.left = false;
    if (e.code === 'KeyD') keys.right = false;
    if (e.code === 'KeyG') keys.fire = false;
  });
}
